import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Phone, PhoneCall, BarChart3, Settings, Clock, Activity, TrendingUp, Users, Bot } from 'lucide-react';

import LiveCallMonitor from './LiveCallMonitor';
import CallHistoryTable from './CallHistoryTable';
import VoiceConfigPanel from './VoiceConfigPanel';
import VoiceAnalytics from './VoiceAnalytics';
import { useVoiceStats } from '../../hooks/useVoiceAPI';
import { useBranding } from '../../context/BrandingContext';

const VoiceDashboard = ({ profileId }) => {
    // [UX] Analytics - первая вкладка по умолчанию
    const [activeTab, setActiveTab] = useState('analytics');
    // Единое окно для всех карточек (перенос из eln-os 3ff76b2c): раньше сервер считал за всё время.
    const [days, setDays] = useState(7);
    const { stats, loading, error, refetch } = useVoiceStats(profileId || 'default', days);
    // Phase 07.1 v1.5: tenant-aware pastel mapping for stat cards
    const brandingContext = useBranding();
    const isFitenko = Boolean(
        brandingContext?.isFitenko ||
        brandingContext?.branding?.instance_name === 'fitenko' ||
        (typeof window !== 'undefined' && (
            window.location.hostname.includes('fitenko') ||
            localStorage.getItem('tenant_id') === 'fitenko' ||
            localStorage.getItem('lastTenant') === 'fitenko'
        ))
    );

    const formatReason = (reason) => {
        if (!reason || reason === 'None') return 'None';
        const map = {
            'customer-ended-call': 'Customer hung up',
            'assistant-forwarded-call': 'Forwarded',
            'silence-timed-out': 'Silence timeout',
            'voicemail': 'Voicemail',
            'assistant-ended-call': 'Completed',
            'customer-did-not-give-microphone-permission': 'Mic denied',
            'pipeline-error-vapi-400': 'Vapi error',
        };
        return map[reason] || reason.replace(/[-_]/g, ' ');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '24rem', color: isFitenko ? '#C8A653' : '#3b82f6' }}>
                <div style={{ width: '4rem', height: '4rem', border: '4px solid #e5e7eb', borderTop: `4px solid ${isFitenko ? '#C8A653' : '#2563eb'}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    const getMetricCardStyle = (startColor, endColor) => {
        if (isFitenko) {
            return {
                backgroundColor: '#ffffff',
                border: '1px solid #E5E1D6',
                borderTop: '3px solid #C8A653',
                borderRadius: '0.75rem',
                color: '#20231F',
                boxShadow: '0 1px 3px rgba(32, 35, 31, 0.04)',
                position: 'relative',
                overflow: 'hidden'
            };
        }
        return {
            border: 'none',
            background: `linear-gradient(135deg, ${startColor} 0%, ${endColor} 100%)`,
            color: 'white',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            position: 'relative',
            overflow: 'hidden'
        };
    };

    const mostCommonReason = stats?.hangupAnalysis ?
        (Object.entries(stats.hangupAnalysis).sort((a, b) => b[1] - a[1])[0]?.[0] || 'None')
        : 'None';

    // Подписи говорят ровно то, что посчитано на сервере (перенос из eln-os 3ff76b2c).
    const totalCalls = stats?.totalCalls || 0;
    const avgCost = Number(stats?.avgCostPerCall || 0).toFixed(2);
    const reasonCount = stats?.hangupAnalysis?.[mostCommonReason] || 0;

    return (
        <div className="w-full" style={{ padding: isFitenko ? '0' : '1.5rem', backgroundColor: 'transparent', minHeight: '100%', color: isFitenko ? '#20231F' : '#0f172a' }}>

            {error && (
                <div style={{ backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', color: '#991b1b' }}>
                    Error loading voice stats: {error}
                </div>
            )}

            {/* Единое окно 7/30/90 дней — управляет всеми карточками */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.8125rem', color: isFitenko ? '#687077' : '#64748b', margin: 0 }}>
                    Metrics for the last {days} days
                </p>
                <div style={{ display: 'inline-flex', gap: '0.25rem', padding: '0.25rem', borderRadius: '0.5rem', backgroundColor: isFitenko ? '#F1EEE4' : '#f1f5f9', border: isFitenko ? '1px solid #E5E1D6' : 'none' }}>
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            type="button"
                            data-testid={`voice-window-${d}`}
                            aria-pressed={days === d}
                            onClick={() => setDays(d)}
                            style={{
                                padding: '0.375rem 0.75rem',
                                borderRadius: '0.375rem',
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: days === d ? '#ffffff' : 'transparent',
                                color: days === d ? '#20231F' : '#687077',
                                boxShadow: days === d ? '0 1px 2px rgba(32, 35, 31, 0.08)' : 'none',
                            }}
                        >
                            {d} days
                        </button>
                    ))}
                </div>
            </div>

            {/* Metric Cards - 4 columns on desktop, 2 on tablet, 1 on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

                {/* Total Cost */}
                <Card style={getMetricCardStyle('#059669', '#047857')} className="w-full flex flex-col justify-between">
                    <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: isFitenko ? '1.125rem 1.25rem 0.5rem' : '1.5rem 1.5rem 0.5rem' }}>
                        <CardTitle style={{
                            fontSize: isFitenko ? '0.75rem' : '0.875rem',
                            fontWeight: '600',
                            textTransform: isFitenko ? 'uppercase' : 'none',
                            letterSpacing: isFitenko ? '0.04em' : 'normal',
                            color: isFitenko ? '#687077' : 'rgba(255,255,255,0.9)'
                        }}>
                            Total Cost
                        </CardTitle>
                        <div style={{
                            backgroundColor: isFitenko ? '#FAF4E5' : 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '0.5rem',
                            padding: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <TrendingUp size={18} color={isFitenko ? '#C8A653' : 'white'} />
                        </div>
                    </CardHeader>
                    <CardContent style={{ padding: isFitenko ? '0 1.25rem 1.125rem' : '0 1.5rem 1.5rem' }}>
                        <div style={{
                            fontSize: '1.875rem',
                            fontWeight: '700',
                            color: isFitenko ? '#20231F' : 'white',
                            fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit'
                        }}>
                            ${stats?.totalCost || '0.00'}
                        </div>
                        <p style={{ fontSize: '0.75rem', color: isFitenko ? '#858481' : 'rgba(255,255,255,0.7)', marginTop: '0.25rem' }}>
                            {totalCalls > 0 ? `Avg $${avgCost} / call (${totalCalls} calls)` : '$0.00 / call (0 calls)'}
                        </p>
                    </CardContent>
                </Card>

                {/* Hangup Analysis */}
                <Card style={getMetricCardStyle('#ea580c', '#c2410c')} className="w-full flex flex-col justify-between">
                    <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: isFitenko ? '1.125rem 1.25rem 0.5rem' : '1.5rem 1.5rem 0.5rem' }}>
                        <CardTitle style={{
                            fontSize: isFitenko ? '0.75rem' : '0.875rem',
                            fontWeight: '600',
                            textTransform: isFitenko ? 'uppercase' : 'none',
                            letterSpacing: isFitenko ? '0.04em' : 'normal',
                            color: isFitenko ? '#687077' : 'rgba(255,255,255,0.9)'
                        }}>
                            Hangup Analysis
                        </CardTitle>
                        <div style={{
                            backgroundColor: isFitenko ? '#FAF4E5' : 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '0.5rem',
                            padding: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <PhoneCall size={18} color={isFitenko ? '#C8A653' : 'white'} />
                        </div>
                    </CardHeader>
                    <CardContent style={{ padding: isFitenko ? '0 1.25rem 1.125rem' : '0 1.5rem 1.5rem' }}>
                        <div style={{
                            fontSize: '1.375rem',
                            fontWeight: '700',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: isFitenko ? '#20231F' : 'white',
                            fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit'
                        }} title={formatReason(mostCommonReason)}>
                            {formatReason(mostCommonReason)}
                        </div>
                        <p style={{ fontSize: '0.75rem', color: isFitenko ? '#858481' : 'rgba(255,255,255,0.7)', marginTop: '0.25rem' }}>
                            {mostCommonReason && mostCommonReason !== 'None' ? `Most common end reason (${reasonCount} calls)` : 'No calls in window'}
                        </p>
                    </CardContent>
                </Card>

                {/* Conversion / Success Rate */}
                <Card style={getMetricCardStyle('#2563eb', '#1d4ed8')} className="w-full flex flex-col justify-between">
                    <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: isFitenko ? '1.125rem 1.25rem 0.5rem' : '1.5rem 1.5rem 0.5rem' }}>
                        <CardTitle style={{
                            fontSize: isFitenko ? '0.75rem' : '0.875rem',
                            fontWeight: '600',
                            textTransform: isFitenko ? 'uppercase' : 'none',
                            letterSpacing: isFitenko ? '0.04em' : 'normal',
                            color: isFitenko ? '#687077' : 'rgba(255,255,255,0.9)'
                        }}>
                            Booking Rate
                        </CardTitle>
                        <div style={{
                            backgroundColor: isFitenko ? '#FAF4E5' : 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '0.5rem',
                            padding: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Activity size={18} color={isFitenko ? '#C8A653' : 'white'} />
                        </div>
                    </CardHeader>
                    <CardContent style={{ padding: isFitenko ? '0 1.25rem 1.125rem' : '0 1.5rem 1.5rem' }}>
                        <div style={{
                            fontSize: '1.875rem',
                            fontWeight: '700',
                            color: isFitenko ? '#20231F' : 'white',
                            fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit'
                        }}>
                            {stats?.conversionRate || 0}%
                        </div>
                        <p style={{ fontSize: '0.75rem', color: isFitenko ? '#858481' : 'rgba(255,255,255,0.7)', marginTop: '0.25rem' }}>
                            {stats?.bookedAppointments || 0} consultations booked
                        </p>
                    </CardContent>
                </Card>

                {/* Avg Duration */}
                <Card style={getMetricCardStyle('#7c3aed', '#6d28d9')} className="w-full flex flex-col justify-between">
                    <CardHeader style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: isFitenko ? '1.125rem 1.25rem 0.5rem' : '1.5rem 1.5rem 0.5rem' }}>
                        <CardTitle style={{
                            fontSize: isFitenko ? '0.75rem' : '0.875rem',
                            fontWeight: '600',
                            textTransform: isFitenko ? 'uppercase' : 'none',
                            letterSpacing: isFitenko ? '0.04em' : 'normal',
                            color: isFitenko ? '#687077' : 'rgba(255,255,255,0.9)'
                        }}>
                            Avg. Handle Time
                        </CardTitle>
                        <div style={{
                            backgroundColor: isFitenko ? '#FAF4E5' : 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '0.5rem',
                            padding: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Clock size={18} color={isFitenko ? '#C8A653' : 'white'} />
                        </div>
                    </CardHeader>
                    <CardContent style={{ padding: isFitenko ? '0 1.25rem 1.125rem' : '0 1.5rem 1.5rem' }}>
                        <div style={{
                            fontSize: '1.875rem',
                            fontWeight: '700',
                            color: isFitenko ? '#20231F' : 'white',
                            fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit'
                        }}>
                            {stats?.avgDuration || '0m 0s'}
                        </div>
                        <p style={{ fontSize: '0.75rem', color: isFitenko ? '#858481' : 'rgba(255,255,255,0.7)', marginTop: '0.25rem' }}>
                            Across {totalCalls} calls
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} style={{ width: '100%' }}>
                <TabsList 
                    className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-1.5 w-full mb-6"
                    style={{
                        height: 'auto',
                        backgroundColor: isFitenko ? '#F1EEE4' : '#f1f5f9',
                        border: isFitenko ? '1px solid #E5E1D6' : 'none',
                        borderRadius: '0.75rem'
                    }}
                >
                    <TabsTrigger value="analytics" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <BarChart3 size={16} /> <span>Analytics</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <Users size={16} /> <span>Call History</span>
                    </TabsTrigger>
                    <TabsTrigger value="monitor" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <Activity size={16} /> <span>Live Monitor</span>
                    </TabsTrigger>
                    <TabsTrigger value="config" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <Settings size={16} /> <span>Configuration</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="analytics">
                    <Card style={{
                        borderTop: isFitenko ? '3px solid #C8A653' : '4px solid #059669',
                        border: '1px solid #E5E1D6',
                        backgroundColor: '#ffffff',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 3px rgba(32, 35, 31, 0.04)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: '#20231F', fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit' }}>Detailed Analytics</CardTitle>
                            <CardDescription style={{ color: '#687077' }}>Deep dive into your voice AI metrics</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <VoiceAnalytics profileId={profileId || 'default'} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="history">
                    <Card style={{
                        borderTop: isFitenko ? '3px solid #C8A653' : '4px solid #7c3aed',
                        border: '1px solid #E5E1D6',
                        backgroundColor: '#ffffff',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 3px rgba(32, 35, 31, 0.04)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: '#20231F', fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit' }}>Call History</CardTitle>
                            <CardDescription style={{ color: '#687077' }}>Full log of all interactions</CardDescription>
                        </CardHeader>
                        <CardContent style={{ padding: '1rem 1.25rem' }}>
                            <CallHistoryTable profileId={profileId || 'default'} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="monitor">
                    <Card style={{
                        borderTop: isFitenko ? '3px solid #C8A653' : '4px solid #3b82f6',
                        border: '1px solid #E5E1D6',
                        backgroundColor: '#ffffff',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 3px rgba(32, 35, 31, 0.04)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: '#20231F', fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit' }}>Live Call Monitor</CardTitle>
                            <CardDescription style={{ color: '#687077' }}>Real-time monitoring</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <LiveCallMonitor profileId={profileId || 'default'} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="config">
                    <Card style={{
                        borderTop: isFitenko ? '3px solid #C8A653' : '4px solid #ea580c',
                        border: '1px solid #E5E1D6',
                        backgroundColor: '#ffffff',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 3px rgba(32, 35, 31, 0.04)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: '#20231F', fontFamily: isFitenko ? 'DM Serif Display, Georgia, serif' : 'inherit' }}>Configuration</CardTitle>
                            <CardDescription style={{ color: '#687077' }}>Manage settings</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <VoiceConfigPanel profileId={profileId || 'default'} />
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default VoiceDashboard;
